/* global axios */
import ApiClient from '../ApiClient';

const compactParams = params =>
  Object.keys(params).reduce((acc, key) => {
    if (params[key] !== undefined) {
      acc[key] = params[key];
    }
    return acc;
  }, {});

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    conversationType,
    sortBy,
    updatedWithin,
    campaignFilter,
    groupFilter,
    socialFilter,
    adminAssigneeScope,
    kanbanFunnelId,
    kanbanStage,
    includeUnassignedInPendentes,
    q,
  }) {
    return axios.get(this.url, {
      params: compactParams({
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        updated_within: updatedWithin,
        campaign_filter: campaignFilter,
        // SiteUp: delimitador de grupos (@g.us) — hide|all|only.
        group_filter: groupFilter,
        // SiteUp: delimitador de canais sociais (não Api/Whatsapp) — hide|all|only.
        social_filter: socialFilter,
        q,
        // SiteUp: escopo global exclusivo para admins na lista de conversas.
        admin_assignee_scope: adminAssigneeScope,
        // SiteUp: Atalhos — ocultar não atribuídas no chip Pendentes (unattended).
        include_unassigned_in_pendentes:
          includeUnassignedInPendentes === false ? false : undefined,
        // SiteUp v654: server-side Kanban membership for quick-filter presets.
        kanban_funnel_id: kanbanFunnelId || undefined,
        kanban_stage: kanbanStage || undefined,
      }),
    });
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      params: compactParams({
        page: payload.page,
        sort_by: payload.sortBy,
        // SiteUp: custom views / advanced filters must honor the same admin
        // assignee scope as the main list ("Todos os usuários" vs mine+unassigned).
        admin_assignee_scope: payload.adminAssigneeScope,
      }),
    });
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({ conversationId, status, snoozedUntil = null }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId }) {
    return axios.post(`${this.url}/${conversationId}/assignments`, {
      assignee_id: agentId,
    });
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  meta({
    inboxId,
    status,
    assigneeType,
    labels,
    teamId,
    conversationType,
    campaignFilter,
    groupFilter,
    socialFilter,
    adminAssigneeScope,
    kanbanFunnelId,
    kanbanStage,
    includeUnassignedInPendentes,
  }) {
    return axios.get(`${this.url}/meta`, {
      params: compactParams({
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
        campaign_filter: campaignFilter,
        // SiteUp: contadores respeitam o delimitador de grupos, igual à lista.
        group_filter: groupFilter,
        // SiteUp: contadores respeitam o delimitador de redes sociais, igual à lista.
        social_filter: socialFilter,
        // SiteUp: contadores respeitam o escopo global exclusivo para admins.
        admin_assignee_scope: adminAssigneeScope,
        include_unassigned_in_pendentes:
          includeUnassignedInPendentes === false ? false : undefined,
        // SiteUp v654: Kanban badge counts (pagination-safe membership meta).
        kanban_funnel_id: kanbanFunnelId || undefined,
        kanban_stage: kanbanStage || undefined,
      }),
    });
  }

  // SiteUp A5: one round-trip for every quick-filter chip count instead of
  // one GET /meta per chip. `base` is shared params applied to every preset
  // unless the preset itself overrides that key; `presets` is
  // `[{ key, ...conversationFinderParams }]`. Falls back to per-chip
  // `meta()` calls on the caller side when this 404s (older backend).
  metaBundle({ base, presets }) {
    return axios.post(`${this.url}/meta_bundle`, { base, presets });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  getAttributes(conversationId) {
    return axios.get(`${this.url}/${conversationId}/custom_attributes`);
  }

  scheduleMessage({
    conversation_id,
    inbox_id,
    content,
    scheduled_at,
    title,
    is_recurrent,
    period,
    files,
  }) {
    if (files && files.length > 0) {
      const formData = new FormData();
      formData.append('scheduled_message[conversation_id]', conversation_id);
      formData.append('scheduled_message[inbox_id]', inbox_id);
      formData.append('scheduled_message[content]', content);
      formData.append('scheduled_message[scheduled_at]', scheduled_at);
      formData.append('scheduled_message[title]', title || '');
      formData.append('scheduled_message[status]', 'pending');
      if (is_recurrent)
        formData.append('scheduled_message[is_recurrent]', is_recurrent);
      if (period) formData.append('scheduled_message[period]', period);
      files.forEach(file => {
        formData.append('scheduled_message[files][]', file);
      });
      return axios.post(
        `${this.url}/${conversation_id}/scheduled_messages`,
        formData
      );
    }
    return axios.post(`${this.url}/${conversation_id}/scheduled_messages`, {
      scheduled_message: {
        conversation_id,
        inbox_id,
        content,
        scheduled_at,
        title,
        status: 'pending',
        is_recurrent,
        period,
      },
    });
  }

  getScheduledMessagesCount(conversationId) {
    return axios.get(`${this.url}/${conversationId}/scheduled_messages/count`, {
      params: { status: 'pending' },
    });
  }

  getScheduledMessages(conversationId) {
    return axios.get(`${this.url}/${conversationId}/scheduled_messages`);
  }

  deleteScheduledMessage(conversationId, messageId) {
    return axios.delete(
      `${this.url}/${conversationId}/scheduled_messages/${messageId}`
    );
  }

  updateScheduledMessage(conversationId, messageId, data) {
    if (data.files && data.files.length > 0) {
      const formData = new FormData();
      formData.append('scheduled_message[inbox_id]', data.inbox_id);
      formData.append('scheduled_message[content]', data.content || '');
      formData.append('scheduled_message[scheduled_at]', data.scheduled_at);
      formData.append('scheduled_message[title]', data.title || '');
      if (data.is_recurrent)
        formData.append('scheduled_message[is_recurrent]', data.is_recurrent);
      if (data.period)
        formData.append('scheduled_message[period]', data.period);
      data.files.forEach(file => {
        formData.append('scheduled_message[files][]', file);
      });
      if (data.remove_file_ids && data.remove_file_ids.length > 0) {
        data.remove_file_ids.forEach(id => {
          formData.append('scheduled_message[remove_file_ids][]', id);
        });
      }
      return axios.patch(
        `${this.url}/${conversationId}/scheduled_messages/${messageId}`,
        formData
      );
    }
    const payload = {
      scheduled_message: {
        inbox_id: data.inbox_id,
        content: data.content,
        scheduled_at: data.scheduled_at,
        title: data.title,
        is_recurrent: data.is_recurrent,
        period: data.period,
      },
    };
    if (data.remove_file_ids && data.remove_file_ids.length > 0) {
      payload.scheduled_message.remove_file_ids = data.remove_file_ids;
    }
    return axios.patch(
      `${this.url}/${conversationId}/scheduled_messages/${messageId}`,
      payload
    );
  }

  requestCopilot(conversationId, body) {
    return axios.post(`${this.url}/${conversationId}/copilot`, body);
  }

  getInboxAssistant(conversationId) {
    return axios.get(`${this.url}/${conversationId}/inbox_assistant`);
  }

  delete(conversationId) {
    return axios.delete(`${this.url}/${conversationId}`);
  }
}

export default new ConversationApi();
